import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PriceTag from '../components/PriceTag';
import WaitlistForm from '../components/WaitlistForm';
import PriceAlertButton from '../components/PriceAlertButton';
import MessageOwnerBox from '../components/MessageOwnerBox';
import { useApi } from '../services/api';
import { formatDateRange, formatPrice } from '../utils/formatters';

// DEMO MODE: real checkout is disabled site-wide ahead of launch (see
// WaitlistForm and backend/src/routes/payments.js). No Stripe.js is loaded
// and no purchase call is made from this page.

export default function ListingDetail() {
  const { id } = useParams();
  const { data, loading, error } = useApi(`/listings/${id}`);
  const [policiesOpen, setPoliciesOpen] = useState(false);

  const listing = data?.listing;

  const { data: descData } = useApi(listing?.property_id ? `/properties/${listing.property_id}/room-descriptions` : null);
  const roomDescription = listing?.unit_number ? descData?.byUnit?.[listing.unit_number] : null;

  if (loading) return (
    <div className="page">
      <Navbar />
      <div className="page-loading">Loading listing…</div>
      <Footer />
    </div>
  );

  if (error || !listing) return (
    <div className="page">
      <Navbar />
      <div className="container" style={{ padding: '2rem 1rem' }}>
        <div className="alert alert-error">Listing not found or unavailable.</div>
      </div>
      <Footer />
    </div>
  );

  const isSold = listing.status === 'sold';
  const isPending = listing.status === 'pending';

  return (
    <div className="page">
      <Navbar />

      {/* DEMO MODE banner — remove before launch */}
      <div style={{ background: 'var(--warning-bg)', borderBottom: '1px solid var(--warning)', padding: '0.75rem 1rem', textAlign: 'center' }}>
        <span style={{ color: 'var(--warning)', fontWeight: 700 }}>DEMO LISTING</span>
        <span style={{ color: 'var(--warning)' }}> — For Testing Purposes Only. Booking not yet available.</span>
      </div>

      <div className="container" style={{ padding: '2rem 1rem', maxWidth: 840 }}>
        {/* Property image */}
        {(listing.image_urls?.[0] || listing.property_image_urls?.[0]) ? (
          <img src={listing.image_urls?.[0] || listing.property_image_urls[0]} alt={listing.property_name}
            style={{ width: '100%', height: 300, objectFit: 'cover', borderRadius: 'var(--radius-md)', marginBottom: '1.5rem' }} />
        ) : (
          <div style={{ width: '100%', height: 220, background: 'linear-gradient(135deg, var(--brand), var(--brand-dark))', borderRadius: 'var(--radius-md)', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--surface)', fontSize: 'var(--text-lg)', fontWeight: 600 }}>
            {listing.property_name}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem', alignItems: 'start' }}>
          {/* Left: details */}
          <div>
            <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 700, marginBottom: '0.25rem' }}>{listing.property_name}</h1>
            <p style={{ color: 'var(--ink-muted)', marginBottom: '1rem' }}>{listing.property_location}</p>

            <div className="card" style={{ padding: '1rem', marginBottom: '1rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem' }}>
                <div>
                  <div style={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ink-muted)', fontWeight: 600 }}>Dates</div>
                  <div style={{ fontWeight: 600, marginTop: '0.2rem' }}>{formatDateRange(listing.check_in_date, listing.check_out_date)}</div>
                </div>
                {listing.unit_number && (
                  <div>
                    <div style={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ink-muted)', fontWeight: 600 }}>Unit</div>
                    <div style={{ fontWeight: 600, marginTop: '0.2rem' }}>{listing.unit_number}</div>
                  </div>
                )}
                <div>
                  <div style={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ink-muted)', fontWeight: 600 }}>Max Guests</div>
                  <div style={{ fontWeight: 600, marginTop: '0.2rem' }}>{listing.max_guests}</div>
                </div>
              </div>
            </div>

            {/* About this unit */}
            <div className="card" style={{ padding: '1.25rem', marginBottom: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.75rem' }}>
                <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--brand-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="var(--brand)" strokeWidth="2">
                    <circle cx="12" cy="12" r="9" strokeLinecap="round" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 11v5m0-8h.01" />
                  </svg>
                </div>
                <h2 style={{ fontSize: 'var(--text-md)', fontWeight: 700, margin: 0 }}>About this unit</h2>
              </div>
              {listing.notes ? (
                <p style={{ fontSize: 'var(--text-base)', color: 'var(--ink)', lineHeight: 1.7, margin: 0 }}>{listing.notes}</p>
              ) : (
                <p style={{ fontSize: 'var(--text-base)', color: 'var(--ink-faint)', fontStyle: 'italic', margin: 0 }}>No description provided.</p>
              )}

              {roomDescription && (
                <div style={{ marginTop: '0.9rem', paddingTop: '0.9rem', borderTop: '1px solid var(--surface-alt)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, fontSize: 'var(--text-base)' }}>{roomDescription.label}</span>
                    {roomDescription.view_type && (
                      <span style={{ fontSize: 'var(--text-xs)', padding: 'var(--space-1) var(--space-2)', borderRadius: 999, background: '#DBEAFE', color: '#1D4ED8', fontWeight: 600 }}>
                        {roomDescription.view_type}
                      </span>
                    )}
                  </div>
                  {roomDescription.description && (
                    <p style={{ fontSize: 'var(--text-sm)', color: 'var(--ink)', margin: '0 0 0.5rem' }}>{roomDescription.description}</p>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', fontSize: 'var(--text-xs)' }}>
                    {roomDescription.bed_config && <span style={{ background: 'var(--surface-alt)', padding: '0.15rem 0.5rem', borderRadius: 'var(--radius-sm)' }}>🛏 {roomDescription.bed_config}</span>}
                    {roomDescription.sleeps && <span style={{ background: 'var(--surface-alt)', padding: '0.15rem 0.5rem', borderRadius: 'var(--radius-sm)' }}>Sleeps {roomDescription.sleeps}</span>}
                    {roomDescription.kitchen && <span style={{ background: 'var(--surface-alt)', padding: '0.15rem 0.5rem', borderRadius: 'var(--radius-sm)' }}>{roomDescription.kitchen === 'full' ? 'Full kitchen' : roomDescription.kitchen === 'partial' ? 'Partial kitchen' : 'Mini fridge + microwave'}</span>}
                    {roomDescription.bathrooms && <span style={{ background: 'var(--surface-alt)', padding: '0.15rem 0.5rem', borderRadius: 'var(--radius-sm)' }}>{roomDescription.bathrooms} bath{roomDescription.bathrooms !== 1 ? 's' : ''}</span>}
                  </div>
                </div>
              )}
            </div>

            {listing.property_description && (
              <p style={{ color: 'var(--ink)', lineHeight: 1.7, marginBottom: '1rem' }}>{listing.property_description}</p>
            )}

            {listing.amenities?.length > 0 && (
              <div>
                <h3 style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Amenities</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                  {listing.amenities.map(a => (
                    <span key={a} style={{ background: 'var(--brand-light)', color: 'var(--brand)', borderRadius: '999px', padding: '0.2rem 0.7rem', fontSize: 'var(--text-sm)', fontWeight: 500 }}>{a}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Policies (collapsible, subtle) */}
            <div style={{ marginTop: '2rem', borderTop: '1px solid var(--surface-alt)', paddingTop: '1rem' }}>
              <button
                type="button"
                onClick={() => setPoliciesOpen(v => !v)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                  fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--ink-faint)',
                }}
              >
                <span style={{ transform: policiesOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s', display: 'inline-block' }}>›</span>
                Policies
              </button>
              {policiesOpen && (
                <div style={{ marginTop: '0.75rem' }}>
                  <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--ink-faint)', marginBottom: '0.4rem' }}>Cancellation Policy</h4>
                  <div className="table-wrap">
                    <table style={{ fontSize: 'var(--text-sm)' }}>
                      <thead>
                        <tr>
                          <th style={{ color: 'var(--ink-faint)', background: 'var(--surface-alt)' }}>Timing</th>
                          <th style={{ color: 'var(--ink-faint)', background: 'var(--surface-alt)' }}>Your Refund</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr><td style={{ color: 'var(--ink-faint)' }}>60+ days before check-in</td><td style={{ color: 'var(--ink-faint)' }}>100% refund</td></tr>
                        <tr><td style={{ color: 'var(--ink-faint)' }}>30–59 days before check-in</td><td style={{ color: 'var(--ink-faint)' }}>50% refund</td></tr>
                        <tr><td style={{ color: 'var(--ink-faint)' }}>Under 30 days</td><td style={{ color: 'var(--ink-faint)' }}>No refund</td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: buy box */}
          <div className="card" style={{ padding: '1.5rem', minWidth: 240, position: 'sticky', top: 80, borderTop: '4px solid var(--brand)', boxShadow: 'var(--shadow-lg)', overflow: 'visible' }}>
            <PriceTag
              currentPrice={listing.current_price}
              ceilingPrice={listing.ceiling_price}
              daysUntilStay={listing.days_until_stay}
              decayPercent={listing.decay_percent}
            />

            <div style={{ marginTop: '1.1rem', paddingTop: '1.1rem', borderTop: '1px dashed var(--border)', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-sm)', color: 'var(--ink-muted)' }}>
                <span>{formatDateRange(listing.check_in_date, listing.check_out_date)}</span>
                <span>{listing.max_guests} guests</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-md)', fontWeight: 700, color: 'var(--ink)' }}>
                <span>Total due today</span>
                <span>{formatPrice(listing.current_price)}</span>
              </div>
            </div>

            <div style={{ marginTop: '1.25rem' }}>
              {isSold ? (
                <div className="alert alert-info" style={{ margin: 0 }}>This listing has been sold.</div>
              ) : isPending ? (
                <div className="alert alert-info" style={{ margin: 0 }}>Someone is currently checking out — check back shortly.</div>
              ) : (
                <>
                  <WaitlistForm price={formatPrice(listing.current_price)} />
                  <div style={{ marginTop: '0.85rem' }}>
                    <PriceAlertButton listingId={listing.id} currentPrice={listing.current_price} />
                  </div>
                  <div style={{ marginTop: '0.85rem' }}>
                    <MessageOwnerBox listingId={listing.id} ownerId={listing.owner_id} />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
